// =============================================================================
// Termo de Adesão DGenny — renderer client-side (preview)
// =============================================================================
// IMPORTANTE: este arquivo é uma CÓPIA do template + lógica de renderização
// que vive em cs-dgenny (functions/src/radar/termoTemplate.ts). Existe aqui
// SÓ pra mostrar preview ao cliente antes do aceite. O backend continua sendo
// a fonte de verdade do conteúdo e do hash. Se mudar o template:
//
//   1. Editar functions/src/radar/termoTemplate.ts no cs-dgenny
//   2. Copiar TERMO_TEMPLATE_MD daqui ↔ lá
//   3. Subir TERMO_VERSION nos dois lugares
//
// Não calculamos hash no client — backend é único que assina.
// =============================================================================

export const TERMO_VERSION = '1.5.1';
export const TERMO_DATA_VIGENCIA = '26/08/2026';

// Deve casar 1:1 com PRAZO_MULTA_MAP de cs-dgenny/functions/src/radar/termoTemplate.ts.
// Trimestral (3) = 100% (contrato integral); demais prazos = 35%.
const PRAZO_MULTA_MAP: Record<number, { percent: number; extenso: string }> = {
  3: { percent: 100, extenso: 'cem' },
  6: { percent: 35, extenso: 'trinta e cinco' },
  12: { percent: 35, extenso: 'trinta e cinco' },
  18: { percent: 35, extenso: 'trinta e cinco' },
};

export interface ContatoAdicionalTermo {
  nome: string;
  cargo?: string;
  email: string;
  telefone: string;
}

export interface RenderTermoData {
  token_aceite: string;
  data_aceite_extenso: string;
  data_hora_aceite: string;
  ip_aceite: string;
  user_agent_aceite: string;

  razao_social_cliente: string;
  cnpj_cliente: string;
  endereco_cliente: string;
  bairro_cliente: string;
  cidade_cliente: string;
  uf_cliente: string;
  cep_cliente: string;

  // Signatário do aceite eletrônico — quem clica em "Aceitar" na Etapa 4.
  // Antes (v1.0.0): era sempre o representante legal. Agora (v1.1.0): pode
  // ser o operacional, o financeiro ou um terceiro indicado manualmente.
  signatario_aceite_cliente: string;
  cargo_signatario_aceite: string;
  cpf_signatario_aceite: string;
  email_signatario_aceite: string;
  telefone_signatario_aceite: string;

  nome_responsavel_operacional: string;
  cargo_responsavel_operacional: string;
  cpf_responsavel_operacional: string;
  email_responsavel_operacional: string;
  telefone_responsavel_operacional: string;

  nome_contato_financeiro: string;
  email_contato_financeiro: string;
  /** WhatsApp do financeiro (obrigatório desde 1.5.1). */
  telefone_contato_financeiro: string;

  /** v1.5.1 — contatos além do líder operacional / financeiro principal.
   *  Vazio/ausente => blocos somem e o markdown fica idêntico ao sem campo. */
  operacionais_adicionais?: ContatoAdicionalTermo[];
  financeiros_adicionais?: ContatoAdicionalTermo[];

  nome_plano: string;
  prazo_minimo_meses: number;
  qtd_assistentes: number;
  qtd_whatsapp: number;
  qtd_usuarios_gestao: number;
  volume_negociacoes_por_assistente: number;
  volume_negociacoes_global: number;
  /** 'mensal' = limite por mês; 'contrato' = limite no período total do contrato. */
  volume_global_periodo: 'mensal' | 'contrato';
  /** Bônus extra de negociações (0 quando não aplicável). */
  volume_bonus_negociacoes: number;
  volume_bonus_periodo: 'mensal' | 'contrato';
  valor_implantacao: string;
  /** Centavos — usado pra montar o texto das parcelas. */
  valor_implantacao_centavos: number;
  num_parcelas_implantacao: number;
  /** Centavos. 0 quando não há entrada. */
  entrada_implantacao_centavos: number;
  /** Centavos do valor de CADA parcela não-entrada (= total quando à vista). */
  valor_parcela_implantacao_centavos: number;
  /** Data do 1º vencimento da implantação, formatada DD/MM/AAAA. */
  data_implantacao: string;
  valor_mensal: string;
  /** Centavos — usado no cálculo do pacote antecipado no cartão (v1.4.0). */
  valor_mensal_centavos: number;
  data_inicio_prevista: string;
  /** Vencimento da 1ª mensalidade negociado (DD/MM/AAAA). '' = a definir. */
  data_primeira_cobranca: string;
  dia_vencimento_mensal: number;
  modelo_cobranca: 'pre_pago' | 'pos_pago';
  /** boleto (default) | pix | cartao (pacote antecipado parcelado — v1.4.0). */
  forma_pagamento: 'boleto' | 'pix' | 'cartao';
  /** Só com cartão: true = implantação dentro do checkout do pacote. */
  cartao_incluir_implantacao: boolean;
  /** Só com cartão e implantação FORA do checkout: forma da implantação. */
  forma_pagamento_implantacao?: 'pix' | 'boleto';
  /** v1.4.1 — pacote antecipado boleto/PIX: contrato pago em N parcelas. */
  pacote_antecipado: boolean;
  /** Nº de parcelas do pacote (quando pacote_antecipado). */
  pacote_parcelas: number;
  /** Vencimentos negociados formatados DD/MM/AAAA, um por parcela. */
  pacote_datas: string[];
  /** true = implantação dentro do total do pacote; false = cobrança própria. */
  pacote_incluir_implantacao: boolean;
  pro_rata_primeira_mensalidade: boolean;
  /** Texto livre que o vendedor digita em Fechar Negócio (deal_terms.
   *  condicoes_especiais). Renderizado após a tabela do item 3 quando presente;
   *  string vazia/ausente => bloco some e o markdown fica idêntico ao sem campo. */
  condicoes_especiais?: string;
}

const TERMO_TEMPLATE_MD = `# Termo de Adesão e Uso da Plataforma dgenny® Compre.AI

**Versão:** 1.5 — Vigente a partir de [DATA_VIGENCIA_TERMO]
**Identificador desta adesão:** [TOKEN_ACEITE]

Este Termo regula o uso da plataforma **dgenny® Compre.AI** pela empresa que está aderindo abaixo ("Cliente"), a partir do aceite eletrônico no link recebido por e-mail. Ele formaliza a proposta comercial aprovada com a equipe da **dgenny Tecnologia LTDA** (CNPJ 60.232.645/0001-81, Rua Frei Caneca, 520, G41PAVGA, Agronômica, Florianópolis/SC, CEP 88.025-053), aqui chamada apenas de "dgenny", e define as condições de uso, pagamento, responsabilidades e proteção de dados.

Ao clicar em "Aceitar", o Cliente confirma que leu, compreendeu e concorda com todas as condições abaixo.

---

## 1. Confirmação dos dados da sua empresa

O Cliente confirma, neste ato, os dados cadastrais usados para esta adesão:

- **Razão social:** [RAZAO_SOCIAL_CLIENTE]
- **CNPJ:** [CNPJ_CLIENTE]
- **Endereço:** [ENDERECO_CLIENTE], [BAIRRO_CLIENTE], [CIDADE_CLIENTE]/[UF_CLIENTE], CEP [CEP_CLIENTE]
- **Signatário do aceite:** [SIGNATARIO_ACEITE_CLIENTE], [CARGO_SIGNATARIO_ACEITE], CPF [CPF_SIGNATARIO_ACEITE], e-mail [EMAIL_SIGNATARIO_ACEITE]

1.1. O Cliente declara que os dados acima são verdadeiros e que o representante indicado possui poderes para aderir a este Termo em nome da empresa.

1.2. Alterações de razão social, endereço, representação legal ou dados de faturamento devem ser comunicadas à dgenny em até 10 (dez) dias corridos.

---

## 2. Contatos para a operação

O Cliente indica abaixo as pessoas que serão os pontos de contato durante o uso da plataforma. Comunicações enviadas a esses e-mails são consideradas válidas para fins contratuais, financeiros e operacionais.

**Signatário do aceite eletrônico:**
- Nome: [SIGNATARIO_ACEITE_CLIENTE]
- Cargo: [CARGO_SIGNATARIO_ACEITE]
- CPF: [CPF_SIGNATARIO_ACEITE]
- E-mail: [EMAIL_SIGNATARIO_ACEITE]
- Telefone: [TELEFONE_SIGNATARIO_ACEITE]

**Responsável operacional (líder de implantação):** profissional que conduzirá a implantação junto ao Customer Success da dgenny, validará entregas e acompanhará a operação contínua.
- Nome: [NOME_RESPONSAVEL_OPERACIONAL]
- Cargo: [CARGO_RESPONSAVEL_OPERACIONAL]
- CPF: [CPF_RESPONSAVEL_OPERACIONAL]
- E-mail: [EMAIL_RESPONSAVEL_OPERACIONAL]
- Telefone: [TELEFONE_RESPONSAVEL_OPERACIONAL]
[BLOCO_OPERACIONAIS_ADICIONAIS]
**Contato financeiro:** recebe boletos, notas fiscais e tratativas de pagamento por e-mail e WhatsApp.
- Nome: [NOME_CONTATO_FINANCEIRO]
- E-mail: [EMAIL_CONTATO_FINANCEIRO]
- WhatsApp: [TELEFONE_CONTATO_FINANCEIRO]
[BLOCO_FINANCEIROS_ADICIONAIS]
O Cliente é responsável por manter esses contatos atualizados.

---

## 3. O que está incluído nesta adesão

O Cliente adere, neste ato, à proposta comercial aprovada com os seguintes termos:

| Item | Detalhamento |
|------|--------------|
| Plano | [NOME_PLANO] |
| Prazo mínimo | [PRAZO_MINIMO_MESES] meses |
| Modelo de cobrança | [MODELO_COBRANCA] |
| Assistentes de IA contratados | [QTD_ASSISTENTES] unidade(s) |
| Contas de WhatsApp inclusas | [QTD_WHATSAPP] (01 por Assistente) |
| Volume de negociações por Assistente / mês | [VOLUME_NEGOCIACOES_POR_ASSISTENTE] negociações |
| Volume global de negociações | [VOLUME_GLOBAL_DETALHE] |
[BLOCO_BONUS_LINHA]| Valor de implantação (total) | R$ [VALOR_IMPLANTACAO] |
| Parcelas da implantação | [NUM_PARCELAS_IMPLANTACAO] |
| Valor das parcelas | [VALOR_PARCELAS_IMPLANTACAO] |
| Vencimento da implantação | [DATA_IMPLANTACAO] |
| Valor da mensalidade | R$ [VALOR_MENSAL] |
| Pro rata da 1ª mensalidade | [PRO_RATA_LABEL] |
| Vencimento da 1ª mensalidade | [DATA_PRIMEIRA_COBRANCA_LABEL] |
| Forma de pagamento | [FORMA_PAGAMENTO_LABEL] |
| Dia de vencimento das mensalidades | Todo dia [DIA_VENCIMENTO_MENSAL] |

Em caso de divergência com materiais comerciais anteriores, este quadro prevalece.

[BLOCO_CONDICOES_ESPECIAIS]**Para evitar dúvidas:**
- **Assistente de IA** é o agente virtual configurado para apoiar cotação, negociação e relacionamento com fornecedores, com 01 conta de WhatsApp Business vinculada.
- **Negociação** é cada conversa iniciada pelo Sistema com um fornecedor, contabilizada por número de telefone único a cada 30 dias.
- **Data de ativação** é a data em que a conta do Cliente é provisionada na plataforma dgenny e a contabilização do plano se inicia — o mesmo dia desta adesão (item 8).

---

## 4. Como o Cliente pode usar a plataforma

4.1. A dgenny concede ao Cliente uma licença de uso da plataforma dgenny® Compre.AI, sob modelo SaaS, durante o prazo desta adesão. A licença é não exclusiva, intransferível e limitada às operações internas de suprimentos do Cliente.

4.2. O Cliente **não pode** sublicenciar, revender, alugar ou ceder o acesso a terceiros.

4.3. Ao usar a plataforma, o Cliente se compromete a:

a) Fornecer, em tempo hábil, as informações, acessos e integrações necessárias à implantação e operação;

b) Usar a plataforma de forma lícita, respeitando especialmente a LGPD, a legislação tributária, consumerista e concorrencial;

c) Não inserir conteúdo ilícito, ofensivo, discriminatório, que viole direitos de terceiros, segredos comerciais ou normas de compliance;

d) Manter sigilo sobre as credenciais de seus usuários — o Cliente é responsável por tudo que for feito por meio delas;

e) Cumprir as políticas de uso aceitável do WhatsApp Business e das demais APIs integradas, sob pena de bloqueio pela respectiva fornecedora;

f) Comunicar imediatamente à dgenny qualquer incidente de segurança, suspeita de uso indevido ou anomalia operacional.

---

## 5. Prazo, renovação e encerramento

5.1. Esta adesão entra em vigor na data do aceite eletrônico e vigora pelo prazo mínimo indicado no item 3. Findo o prazo, é renovada automaticamente por períodos de 12 (doze) meses, salvo manifestação em contrário de qualquer das partes com pelo menos 30 (trinta) dias de antecedência do término do período em curso.

[BLOCO_MULTA_RESCISORIA]
5.3. **A dgenny pode encerrar** esta adesão em caso de inadimplência superior a 30 (trinta) dias, uso indevido da plataforma, descumprimento deste Termo ou determinação judicial/administrativa que inviabilize a continuidade dos serviços.

5.4. O encerramento, por qualquer motivo, não afasta as obrigações já assumidas — especialmente as relativas a pagamento, confidencialidade e proteção de dados.

---

## 6. Valores e pagamentos

6.1. **Implantação:** [TEXTO_IMPLANTACAO]

6.2. **Mensalidade:** R$ [VALOR_MENSAL], devida a partir da Data de Ativação. A 1ª mensalidade vence na data indicada em "Primeira cobrança" abaixo; as seguintes vencem todo dia [DIA_VENCIMENTO_MENSAL].[BLOCO_PRO_RATA]

[BLOCO_PRIMEIRA_COBRANCA]

6.3. **Modelo de cobrança:** [TEXTO_MODELO_COBRANCA]

6.4. **Forma de pagamento:** [TEXTO_FORMA_PAGAMENTO]

6.5. **Atraso no pagamento:** gera multa de 2% (dois por cento) sobre o valor devido, juros de 1% (um por cento) ao mês e atualização pelo IPCA/IBGE pro rata die. A partir de 15 (quinze) dias de atraso, a dgenny pode suspender o acesso à plataforma até a regularização integral do débito.

6.6. **Reajuste:** a mensalidade é reajustada anualmente, a partir do 13º (décimo terceiro) mês de vigência, pela variação acumulada do IPCA/IBGE — ou, na sua falta, por outro índice oficial que o substitua.

---

## 7. Recursos adicionais

Caso o Cliente queira expandir o escopo durante a vigência, os valores vigentes nesta data são (sujeitos a reajuste anual):

- **Assistente de IA adicional:** R$ 1.300,00 (um mil e trezentos reais) por mês, incluindo 01 conta de WhatsApp;
- **Lote de 100 negociações adicionais dentro do mês:** R$ 400,00 (quatrocentos reais).

A contratação de recursos adicionais pode ser formalizada por aceite simplificado no painel da plataforma ou por troca de e-mail entre as partes, passando a integrar este Termo a partir do mês subsequente.

---

## 8. Ativação e onboarding

8.1. **Data de Ativação:** é a data desta adesão. A conta do Cliente é provisionada na plataforma dgenny no mesmo dia do aceite eletrônico, e a contabilização do plano (volumes, prazo mínimo e mensalidades, conforme item 6) se inicia a partir dela.

8.2. **Início do onboarding:** a dgenny inicia o onboarding (reunião de kickoff e plano de implantação) em até 7 (sete) dias úteis a contar da assinatura deste Termo, conforme a disponibilidade de agenda do Cliente. O Cliente se compromete a disponibilizar o responsável operacional e os insumos do plano de implantação nesse prazo.

8.3. Atrasos causados pelo Cliente na entrega de informações, acessos, integrações ou validações não suspendem a contabilização do plano nem adiam o vencimento das mensalidades, que seguem a Data de Ativação.

8.4. O responsável operacional indicado no item 2 é o ponto focal do Cliente para onboarding e operação contínua, aprovando configurações, scripts de negociação e parametrizações dos Assistentes.

---

## 9. Disponibilidade e limites de responsabilidade

9.1. A dgenny envidará seus melhores esforços para manter a plataforma disponível 24 (vinte e quatro) horas por dia, 7 (sete) dias por semana, ressalvadas janelas de manutenção programada, eventos de força maior, caso fortuito e interrupções decorrentes de serviços de terceiros.

9.2. **A dgenny não se responsabiliza** por interrupções, falhas, lentidão, bloqueios ou indisponibilidades causadas por APIs ou serviços de terceiros — incluindo, sem se limitar a, Meta/WhatsApp, OpenAI, Google, Microsoft, Amazon Web Services e provedores de telecomunicações.

9.3. A dgenny também não responde por (i) decisões comerciais que o Cliente tome com base em resultados gerados pelos Assistentes de IA; (ii) prejuízos indiretos, lucros cessantes ou perda de oportunidade; (iii) uso da plataforma em desacordo com este Termo.

9.4. A responsabilidade civil total da dgenny, por qualquer causa, está limitada ao valor total efetivamente pago pelo Cliente nos 12 (doze) meses imediatamente anteriores ao evento que deu origem à responsabilização.

9.5. Esta é uma relação estritamente empresarial (B2B). Não se aplicam as disposições do Código de Defesa do Consumidor.

---

## 10. Proteção de dados (LGPD)

10.1. As partes atuam em conformidade com a Lei nº 13.709/2018 ("LGPD") e demais normas aplicáveis ao tratamento de dados pessoais.

10.2. Para os fins da LGPD:
- O **Cliente** atua como **Controlador** dos dados pessoais inseridos ou processados na plataforma;
- A **dgenny** atua como **Operadora**, tratando esses dados apenas para executar este Termo e conforme instruções do Cliente.

10.3. Cada parte adota medidas técnicas e administrativas razoáveis para proteger os dados pessoais contra acessos não autorizados, perda, alteração ou destruição.

10.4. Incidentes de segurança envolvendo dados pessoais devem ser comunicados à outra parte em até 24 (vinte e quatro) horas a contar do conhecimento do fato.

10.5. Encerrada esta adesão, a dgenny mantém os dados do Cliente em sua infraestrutura por 90 (noventa) dias, apenas para reativação ou exportação. Depois desse prazo, os dados são definitivamente eliminados, salvo obrigação legal de retenção.

---

## 11. Confidencialidade

11.1. Cada parte mantém sigilo absoluto sobre as informações confidenciais a que tiver acesso em razão desta adesão — incluindo dados comerciais, financeiros, técnicos, estratégicos, listas de fornecedores, preços e métodos de negociação.

11.2. Essa obrigação vigora durante todo o uso da plataforma e por 5 (cinco) anos após o encerramento, por qualquer motivo.

---

## 12. Propriedade intelectual

12.1. Todos os direitos sobre o software dgenny® Compre.AI — incluindo código-fonte, algoritmos, modelos de IA, prompts, base de conhecimento, interfaces, marcas, logotipos e materiais correlatos — pertencem exclusivamente à dgenny.

12.2. Este Termo concede ao Cliente apenas uma licença de uso temporária, não exclusiva e intransferível durante a vigência.

12.3. Customizações, melhorias ou ajustes funcionais que a dgenny venha a desenvolver, mesmo que inspirados em demandas específicas do Cliente, são de propriedade definitiva e perpétua da dgenny e podem ser incorporados ao produto padrão.

12.4. Os dados de negociação inseridos ou gerados pelo Cliente durante o uso da plataforma permanecem de titularidade do Cliente. A dgenny pode utilizá-los de forma anonimizada e agregada para fins estatísticos, treinamento de modelos e melhoria contínua do produto.

---

## 13. Regras gerais e foro

13.1. Este Termo, junto com a proposta comercial aprovada, é o acordo integral entre as partes sobre o seu objeto, superando entendimentos, propostas ou negociações anteriores, escritos ou verbais.

13.2. A eventual tolerância de uma parte quanto ao descumprimento de qualquer obrigação pela outra não configura novação, renúncia ou alteração.

13.3. A nulidade ou ineficácia de qualquer item deste Termo não afeta os demais, que permanecem válidos e exequíveis.

13.4. Não há vínculo societário, trabalhista, de representação ou de exclusividade entre as partes.

13.5. Fica eleito o foro da Comarca de **Florianópolis/SC** para dirimir quaisquer controvérsias oriundas deste Termo, com renúncia expressa a qualquer outro, por mais privilegiado que seja.

---

## 14. Aceite eletrônico

Ao concluir a adesão no link recebido por e-mail, o Cliente declara que:

a) Leu integralmente este Termo, compreendeu seu conteúdo e o aceita de livre e espontânea vontade;

b) Confirmou e validou os dados da empresa, dos contatos e da proposta comercial nos itens 1, 2 e 3;

c) Possui poderes para aderir a este Termo em nome da empresa.

O aceite eletrônico, registrado por link único com token de acesso individualizado, equivale à assinatura manuscrita para todos os fins legais, nos termos da Medida Provisória nº 2.200-2/2001 e da Lei nº 14.063/2020, sendo plenamente válido, vinculante e exequível entre as partes.

**Registros do aceite (para fins probatórios):**

| Dado | Valor |
|------|-------|
| Token de aceite | [TOKEN_ACEITE] |
| Data e hora do aceite (UTC-3) | [DATA_HORA_ACEITE] |
| Endereço IP do aceite | [IP_ACEITE] |
| User-Agent | [USER_AGENT_ACEITE] |
| Hash do documento | [HASH_DOCUMENTO] |

---

*Adesão registrada eletronicamente em [DATA_ACEITE] por [SIGNATARIO_ACEITE_CLIENTE] ([CARGO_SIGNATARIO_ACEITE]), em nome de [RAZAO_SOCIAL_CLIENTE], mediante token [TOKEN_ACEITE].*
`;

function buildVolumeGlobalDetalhe(data: RenderTermoData): string {
  const sufixo = data.volume_global_periodo === 'contrato' ? 'no período do contrato' : '/ mês';
  return `${data.volume_negociacoes_global} negociações ${sufixo}`;
}

function buildBlocoBonusLinha(data: RenderTermoData): string {
  if (!data.volume_bonus_negociacoes || data.volume_bonus_negociacoes <= 0) return '';
  const sufixo = data.volume_bonus_periodo === 'contrato' ? 'no período do contrato' : '/ mês';
  return `| Bônus de negociações | ${data.volume_bonus_negociacoes} negociações ${sufixo} |\n`;
}

// v1.5.1 — contatos adicionais (item 2). Lista vazia => '' (markdown idêntico
// ao 1.5.0 para o mesmo input). Com itens, cada um vira uma linha.
function buildBlocoContatosAdicionais(
  titulo: string,
  contatos: ContatoAdicionalTermo[] | undefined,
  rotuloTelefone: string,
): string {
  const lista = (contatos || []).filter((c) => c && (c.nome || c.email));
  if (lista.length === 0) return '';
  const linhas = lista.map((c) => {
    const cargo = (c.cargo || '').trim();
    const nome = cargo ? `${c.nome} (${cargo})` : c.nome;
    return `- ${nome} — e-mail ${c.email}, ${rotuloTelefone} ${c.telefone}`;
  });
  return `\n**${titulo}:**\n${linhas.join('\n')}\n`;
}

function buildBlocoOperacionaisAdicionais(data: RenderTermoData): string {
  return buildBlocoContatosAdicionais('Demais contatos operacionais', data.operacionais_adicionais, 'telefone');
}

function buildBlocoFinanceirosAdicionais(data: RenderTermoData): string {
  return buildBlocoContatosAdicionais('Demais contatos financeiros', data.financeiros_adicionais, 'WhatsApp');
}

function buildBlocoCondicoesEspeciais(data: RenderTermoData): string {
  const txt = (data.condicoes_especiais || '').trim();
  if (!txt) return '';
  return `**Condições especiais negociadas:** ${txt}\n\n`;
}

function buildNumParcelasImplantacao(data: RenderTermoData): string {
  const n = Math.max(1, data.num_parcelas_implantacao || 1);
  return n === 1 ? '1 (à vista)' : `${n} parcelas`;
}

function buildValorParcelasImplantacao(data: RenderTermoData): string {
  const n = Math.max(1, data.num_parcelas_implantacao || 1);
  const entradaCent = data.entrada_implantacao_centavos || 0;
  const parcelaCent =
    data.valor_parcela_implantacao_centavos || data.valor_implantacao_centavos || 0;
  if (n === 1) {
    return `R$ ${formatCentavosBR(data.valor_implantacao_centavos || 0)} à vista`;
  }
  if (entradaCent > 0) {
    return `Entrada de R$ ${formatCentavosBR(entradaCent)} + ${n - 1} x R$ ${formatCentavosBR(parcelaCent)}`;
  }
  return `${n} x R$ ${formatCentavosBR(parcelaCent)}`;
}

function buildTextoImplantacao(data: RenderTermoData): string {
  const n = Math.max(1, data.num_parcelas_implantacao || 1);
  const total = `R$ ${data.valor_implantacao}`;
  const venc = data.data_implantacao || data.data_inicio_prevista || '(a definir)';
  if (n === 1) {
    return `${total} em parcela única, com vencimento em ${venc}.`;
  }
  const detalhe = buildValorParcelasImplantacao(data);
  return `${total} dividido em ${n} parcelas (${detalhe}), com primeiro vencimento em ${venc}.`;
}

function buildTextoModeloCobranca(data: RenderTermoData): string {
  if (data.modelo_cobranca === 'pos_pago') {
    return (
      'esta adesão é cobrada em modelo **pós-pago** — cada mensalidade é emitida com ' +
      'vencimento APÓS o mês de serviço a que se refere.'
    );
  }
  return (
    'esta adesão é cobrada em modelo **pré-pago** — cada mensalidade é emitida com ' +
    'vencimento ANTES do mês de serviço a que se refere.'
  );
}

function buildBlocoProRata(data: RenderTermoData): string {
  if (!data.pro_rata_primeira_mensalidade) return '';
  return (
    ' **A primeira mensalidade será proporcional (pro rata)** aos dias entre a Data de ' +
    'Ativação e o último dia do mês da ativação.'
  );
}

function formatModeloCobrancaLabel(modelo: 'pre_pago' | 'pos_pago'): string {
  return modelo === 'pos_pago' ? 'Pós-pago' : 'Pré-pago';
}

// v1.4.0 — número de parcelas do pacote antecipado no cartão: uma por
// mensalidade do prazo mínimo, teto de 12 (limite do parcelamento).
function cardPackageParcelas(data: RenderTermoData): number {
  return Math.min(Math.max(1, data.prazo_minimo_meses || 1), 12);
}

// v1.4.1 — total do pacote antecipado boleto/PIX (contrato inteiro): prazo ×
// mensalidade (+ implantação quando inclusa no pacote).
function pacoteTotalCentavos(data: RenderTermoData): number {
  const mensal = (data.prazo_minimo_meses || 0) * (data.valor_mensal_centavos || 0);
  const impl = data.pacote_incluir_implantacao ? data.valor_implantacao_centavos || 0 : 0;
  return mensal + impl;
}

function buildFormaPagamentoLabel(data: RenderTermoData): string {
  if (data.forma_pagamento === 'cartao') {
    return `Cartão de crédito — pacote antecipado em ${cardPackageParcelas(data)}x`;
  }
  const base = data.forma_pagamento === 'pix' ? 'PIX' : 'Boleto';
  if (data.pacote_antecipado) {
    return `${base} — pacote antecipado em ${data.pacote_parcelas}x`;
  }
  return base;
}

function buildDataPrimeiraCobrancaLabel(data: RenderTermoData): string {
  return data.data_primeira_cobranca || 'a definir junto ao time financeiro da dgenny';
}

function buildTextoFormaPagamento(data: RenderTermoData): string {
  const fin = 'conforme dados enviados ao contato financeiro indicado no item 2.';
  if (data.pacote_antecipado && data.forma_pagamento !== 'cartao') {
    const formaTxt = data.forma_pagamento === 'pix' ? 'PIX' : 'boleto bancário (com QR PIX)';
    const n = data.pacote_parcelas;
    const total = pacoteTotalCentavos(data);
    const parcela = Math.floor(total / n);
    const datas = data.pacote_datas.join(', ');
    let texto =
      `${formaTxt}, em **pagamento antecipado do contrato**: ${n} parcela(s) de ` +
      `aproximadamente R$ ${formatCentavosBR(parcela)} (total R$ ${formatCentavosBR(total)}), ` +
      `com vencimentos em ${datas}`;
    if (data.pacote_incluir_implantacao && (data.valor_implantacao_centavos || 0) > 0) {
      texto += ', com a implantação incluída no valor do pacote';
    } else if ((data.valor_implantacao_centavos || 0) > 0) {
      texto += '. A implantação é cobrada à parte, conforme item 6.1';
    }
    return `${texto}, ${fin}`;
  }
  if (data.forma_pagamento === 'pix') {
    return `PIX, ${fin}`;
  }
  if (data.forma_pagamento === 'cartao') {
    const n = cardPackageParcelas(data);
    let texto =
      `cartão de crédito, em **pagamento antecipado do pacote**: as ${n} mensalidades do ` +
      `prazo mínimo são pagas num único checkout, parcelado em ${n}x no cartão pela operadora`;
    if (data.cartao_incluir_implantacao && (data.valor_implantacao_centavos || 0) > 0) {
      texto += ', com a implantação incluída no valor do pacote';
    } else if ((data.valor_implantacao_centavos || 0) > 0) {
      const formaImpl = data.forma_pagamento_implantacao === 'boleto' ? 'boleto bancário' : 'PIX';
      texto += `. A implantação é cobrada à parte, via ${formaImpl}, conforme item 6.1`;
    }
    return `${texto}, ${fin}`;
  }
  return `boleto bancário (com QR PIX), ${fin}`;
}

// v1.4.0 — composição da PRIMEIRA cobrança, DERIVADA dos dados do fechamento
// (datas de implantação e 1ª mensalidade, parcelas, pro rata, forma de
// pagamento). Sempre presente no item 6.2 — nunca retorna vazio.
function buildBlocoPrimeiraCobranca(data: RenderTermoData): string {
  const temImpl = (data.valor_implantacao_centavos || 0) > 0;
  const dataPrimeira = data.data_primeira_cobranca;
  const vencPrimeira = dataPrimeira || 'a definir junto ao time financeiro da dgenny';
  const mensalidadeDetalhe = data.pro_rata_primeira_mensalidade
    ? 'proporcional (pro rata)'
    : `integral (R$ ${data.valor_mensal})`;

  if (data.forma_pagamento === 'cartao') {
    const n = cardPackageParcelas(data);
    const inclui = data.cartao_incluir_implantacao && temImpl;
    const totalCent =
      n * (data.valor_mensal_centavos || 0) + (inclui ? data.valor_implantacao_centavos || 0 : 0);
    let texto =
      `**Primeira cobrança:** pagamento antecipado do pacote — checkout único de ` +
      `R$ ${formatCentavosBR(totalCent)}, parcelado em ${n}x no cartão de crédito, ` +
      `com vencimento em ${vencPrimeira}.`;
    if (inclui) {
      texto += ' A implantação está incluída no valor do pacote.';
    } else if (temImpl) {
      texto += ' A implantação é cobrada à parte, conforme itens 6.1 e 6.4.';
    }
    return texto;
  }

  if (data.pacote_antecipado) {
    const n = data.pacote_parcelas;
    const total = pacoteTotalCentavos(data);
    const primeira = data.pacote_datas[0] || vencPrimeira;
    let texto =
      `**Primeira cobrança:** pagamento antecipado do contrato — ${n} parcela(s) ` +
      `totalizando R$ ${formatCentavosBR(total)}, com a 1ª vencendo em ${primeira}.`;
    if (data.pacote_incluir_implantacao && temImpl) {
      texto += ' A implantação está incluída no valor do pacote.';
    } else if (temImpl) {
      texto += ' A implantação é cobrada à parte, conforme item 6.1.';
    }
    return texto;
  }

  const dataImpl = data.data_implantacao || data.data_inicio_prevista || '';
  if (temImpl && dataPrimeira && dataImpl === dataPrimeira) {
    const implDetalhe = buildValorParcelasImplantacao(data);
    return (
      `**Primeira cobrança:** vencimento em ${dataPrimeira}, contemplando a implantação ` +
      `(${implDetalhe}) e a 1ª mensalidade, ${mensalidadeDetalhe}.`
    );
  }
  let texto =
    `**Primeira cobrança:** 1ª mensalidade com vencimento em ${vencPrimeira}, ` +
    `${mensalidadeDetalhe}.`;
  if (temImpl) {
    texto += ' A implantação é cobrada à parte, conforme item 6.1.';
  }
  return texto;
}

function buildBlocoMultaRescisoria(prazoMeses: number): string {
  if (prazoMeses <= 1) return '';
  const multa = PRAZO_MULTA_MAP[prazoMeses] ?? { percent: 35, extenso: 'trinta e cinco' };
  return (
    `5.2. **Se o Cliente encerrar antes do término do prazo mínimo**, pagará à dgenny, ` +
    `a título de multa compensatória pela quebra de fidelidade, o equivalente a ` +
    `${multa.percent}% (${multa.extenso} por cento) do valor das mensalidades vincendas ` +
    `até o fim do prazo mínimo, sem prejuízo das mensalidades já vencidas e da ` +
    `implantação eventualmente parcelada.\n\n`
  );
}

/** Renderiza preview do termo no client (sem calcular hash — backend faz). */
export function renderTermoPreview(data: RenderTermoData): string {
  let md = TERMO_TEMPLATE_MD;
  md = md.replace('[BLOCO_MULTA_RESCISORIA]', buildBlocoMultaRescisoria(data.prazo_minimo_meses));
  md = md.replace('[BLOCO_BONUS_LINHA]', buildBlocoBonusLinha(data));
  md = md.replace('[BLOCO_CONDICOES_ESPECIAIS]', buildBlocoCondicoesEspeciais(data));
  md = md.replace('[BLOCO_OPERACIONAIS_ADICIONAIS]', buildBlocoOperacionaisAdicionais(data));
  md = md.replace('[BLOCO_FINANCEIROS_ADICIONAIS]', buildBlocoFinanceirosAdicionais(data));
  md = md.replace('[BLOCO_PRO_RATA]', buildBlocoProRata(data));
  md = md.replace('[BLOCO_PRIMEIRA_COBRANCA]', buildBlocoPrimeiraCobranca(data));
  md = md.replace('[TEXTO_IMPLANTACAO]', buildTextoImplantacao(data));
  md = md.replace('[TEXTO_MODELO_COBRANCA]', buildTextoModeloCobranca(data));
  md = md.replace('[TEXTO_FORMA_PAGAMENTO]', buildTextoFormaPagamento(data));
  md = md.replace(/\[VOLUME_GLOBAL_DETALHE\]/g, buildVolumeGlobalDetalhe(data));
  md = md.replace(/\[MODELO_COBRANCA\]/g, formatModeloCobrancaLabel(data.modelo_cobranca));
  md = md.replace(/\[NUM_PARCELAS_IMPLANTACAO\]/g, buildNumParcelasImplantacao(data));
  md = md.replace(/\[VALOR_PARCELAS_IMPLANTACAO\]/g, buildValorParcelasImplantacao(data));
  md = md.replace(/\[DATA_IMPLANTACAO\]/g, data.data_implantacao || data.data_inicio_prevista || '');
  md = md.replace(/\[PRO_RATA_LABEL\]/g, data.pro_rata_primeira_mensalidade ? 'Sim' : 'Não');
  md = md.replace(/\[DATA_PRIMEIRA_COBRANCA_LABEL\]/g, buildDataPrimeiraCobrancaLabel(data));
  md = md.replace(/\[FORMA_PAGAMENTO_LABEL\]/g, buildFormaPagamentoLabel(data));
  md = md.replace(/\[DATA_VIGENCIA_TERMO\]/g, TERMO_DATA_VIGENCIA);
  md = md.replace(/\[TOKEN_ACEITE\]/g, data.token_aceite);
  md = md.replace(/\[DATA_ACEITE\]/g, data.data_aceite_extenso);
  md = md.replace(/\[DATA_HORA_ACEITE\]/g, data.data_hora_aceite);
  md = md.replace(/\[IP_ACEITE\]/g, data.ip_aceite || '(será capturado ao aceitar)');
  md = md.replace(
    /\[USER_AGENT_ACEITE\]/g,
    data.user_agent_aceite || '(será capturado ao aceitar)',
  );
  md = md.replace(/\[HASH_DOCUMENTO\]/g, '(calculado no servidor após o aceite)');

  md = md.replace(/\[RAZAO_SOCIAL_CLIENTE\]/g, data.razao_social_cliente);
  md = md.replace(/\[CNPJ_CLIENTE\]/g, data.cnpj_cliente);
  md = md.replace(/\[ENDERECO_CLIENTE\]/g, data.endereco_cliente);
  md = md.replace(/\[BAIRRO_CLIENTE\]/g, data.bairro_cliente);
  md = md.replace(/\[CIDADE_CLIENTE\]/g, data.cidade_cliente);
  md = md.replace(/\[UF_CLIENTE\]/g, data.uf_cliente);
  md = md.replace(/\[CEP_CLIENTE\]/g, data.cep_cliente);

  md = md.replace(/\[SIGNATARIO_ACEITE_CLIENTE\]/g, data.signatario_aceite_cliente);
  md = md.replace(/\[CARGO_SIGNATARIO_ACEITE\]/g, data.cargo_signatario_aceite);
  md = md.replace(/\[CPF_SIGNATARIO_ACEITE\]/g, data.cpf_signatario_aceite);
  md = md.replace(/\[EMAIL_SIGNATARIO_ACEITE\]/g, data.email_signatario_aceite);
  md = md.replace(/\[TELEFONE_SIGNATARIO_ACEITE\]/g, data.telefone_signatario_aceite);

  md = md.replace(/\[NOME_RESPONSAVEL_OPERACIONAL\]/g, data.nome_responsavel_operacional);
  md = md.replace(/\[CARGO_RESPONSAVEL_OPERACIONAL\]/g, data.cargo_responsavel_operacional);
  md = md.replace(/\[CPF_RESPONSAVEL_OPERACIONAL\]/g, data.cpf_responsavel_operacional);
  md = md.replace(/\[EMAIL_RESPONSAVEL_OPERACIONAL\]/g, data.email_responsavel_operacional);
  md = md.replace(/\[TELEFONE_RESPONSAVEL_OPERACIONAL\]/g, data.telefone_responsavel_operacional);

  md = md.replace(/\[NOME_CONTATO_FINANCEIRO\]/g, data.nome_contato_financeiro);
  md = md.replace(/\[EMAIL_CONTATO_FINANCEIRO\]/g, data.email_contato_financeiro);
  md = md.replace(/\[TELEFONE_CONTATO_FINANCEIRO\]/g, data.telefone_contato_financeiro);

  md = md.replace(/\[NOME_PLANO\]/g, data.nome_plano);
  md = md.replace(/\[PRAZO_MINIMO_MESES\]/g, String(data.prazo_minimo_meses));
  md = md.replace(/\[QTD_ASSISTENTES\]/g, String(data.qtd_assistentes));
  md = md.replace(/\[QTD_WHATSAPP\]/g, String(data.qtd_whatsapp));
  md = md.replace(/\[QTD_USUARIOS_GESTAO\]/g, String(data.qtd_usuarios_gestao));
  md = md.replace(
    /\[VOLUME_NEGOCIACOES_POR_ASSISTENTE\]/g,
    String(data.volume_negociacoes_por_assistente),
  );
  md = md.replace(/\[VOLUME_NEGOCIACOES_GLOBAL\]/g, String(data.volume_negociacoes_global));
  md = md.replace(/\[VALOR_IMPLANTACAO\]/g, data.valor_implantacao);
  md = md.replace(/\[VALOR_MENSAL\]/g, data.valor_mensal);
  md = md.replace(/\[DATA_INICIO_PREVISTA\]/g, data.data_inicio_prevista);
  md = md.replace(/\[DIA_VENCIMENTO_MENSAL\]/g, String(data.dia_vencimento_mensal));

  return md;
}

// Helpers de formatação
export function formatCentavosBR(centavos: number): string {
  const reais = centavos / 100;
  return reais.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatDateBR(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function formatDataAceiteExtenso(d: Date): string {
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
}

export function formatDataHoraBR(d: Date): string {
  const off = new Date(d.getTime() - 3 * 60 * 60 * 1000);
  const dd = String(off.getUTCDate()).padStart(2, '0');
  const mm = String(off.getUTCMonth() + 1).padStart(2, '0');
  const yy = off.getUTCFullYear();
  const hh = String(off.getUTCHours()).padStart(2, '0');
  const mi = String(off.getUTCMinutes()).padStart(2, '0');
  const ss = String(off.getUTCSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}:${ss}`;
}

export function deriveNomePlano(prazoMeses: number, volumeGlobal: number): string {
  let tipo: string;
  if (prazoMeses <= 1) tipo = 'Plano Mensal';
  else if (prazoMeses === 3) tipo = 'Plano Trimestral';
  else if (prazoMeses === 6) tipo = 'Plano Semestral';
  else if (prazoMeses === 12) tipo = 'Plano Anual';
  else if (prazoMeses >= 18) tipo = 'Plano Premium';
  else tipo = `Plano ${prazoMeses}m`;
  let tier: string;
  if (volumeGlobal < 500) tier = 'Start';
  else if (volumeGlobal < 2000) tier = 'Pro';
  else tier = 'Enterprise';
  return `${tipo} ${tier}`;
}

// =============================================================================
// v1.0.0 (2026-08-26) — TERMO DE RECURSO ADICIONAL (aditivo)
// Documento próprio para a contratação de um recurso adicional por cliente
// que JÁ aceitou o Termo de Adesão (seção 7 do termo). Referencia o termo
// original em vez de repeti-lo: sem fidelidade própria, sem tabela de plano.
// Mesmas regras de reprodutibilidade do termo principal: pipeline de replaces
// IDÊNTICO ao espelho em dgenny-adesao/src/services/termoRenderer.ts e
// [HASH_DOCUMENTO] literal no momento do sha256().
// =============================================================================

export const TERMO_ADITIVO_VERSION = '1.0.0';
export const TERMO_ADITIVO_DATA_VIGENCIA = '26/08/2026';

export interface RenderTermoAditivoData {
  // Metadados do aceite
  token_aceite: string;
  data_aceite_extenso: string;
  data_hora_aceite: string;
  ip_aceite: string;
  user_agent_aceite: string;
  // Termo original
  parent_adhesion_id: string;
  parent_document_hash: string;
  parent_accepted_at: string; // DD/MM/AAAA
  // Empresa contratante (a que paga este recurso — pode ser filial do grupo)
  razao_social_cliente: string;
  cnpj_cliente: string;
  endereco_cliente: string;
  bairro_cliente: string;
  cidade_cliente: string;
  uf_cliente: string;
  cep_cliente: string;
  // Contatos
  signatario_aceite_cliente: string;
  cargo_signatario_aceite: string;
  cpf_signatario_aceite: string;
  email_signatario_aceite: string;
  telefone_signatario_aceite: string;
  nome_responsavel_operacional: string;
  cargo_responsavel_operacional: string;
  email_responsavel_operacional: string;
  telefone_responsavel_operacional: string;
  nome_contato_financeiro: string;
  email_contato_financeiro: string;
  telefone_contato_financeiro: string;
  operacionais_adicionais?: ContatoAdicionalTermo[];
  financeiros_adicionais?: ContatoAdicionalTermo[];
  // Recurso
  nome_recurso: string;
  descricao_recurso: string;
  valor_mensal: string; // formatado BR
  valor_implantacao_centavos: number;
  data_ativacao: string; // DD/MM/AAAA
  dia_vencimento_mensal: number;
  pro_rata_primeira_mensalidade: boolean;
  conta_separada: boolean;
  condicoes_especiais?: string;
}

const TERMO_ADITIVO_TEMPLATE_MD = `# Termo de Contratação de Recurso Adicional — dgenny® Compre.AI

**Versão:** 1.0 — Vigente a partir de [DATA_VIGENCIA_TERMO]
**Identificador desta contratação:** [TOKEN_ACEITE]
**Termo de Adesão original:** aceito em [PARENT_ACCEPTED_AT] (identificador [PARENT_ADHESION_ID], hash [PARENT_DOCUMENT_HASH])

Este documento formaliza a contratação de um recurso adicional pela empresa indicada abaixo ("Cliente") junto à **dgenny Tecnologia LTDA** (CNPJ 60.232.645/0001-81, Rua Frei Caneca, 520, G41PAVGA, Agronômica, Florianópolis/SC, CEP 88.025-053), aqui chamada apenas de "dgenny", nos termos da seção 7 ("Recursos adicionais") do Termo de Adesão e Uso da Plataforma dgenny® Compre.AI já aceito pelo Cliente. O Termo de Adesão original permanece integralmente em vigor e passa a abranger também o recurso aqui descrito.

Ao clicar em "Aceitar", o Cliente confirma que leu, compreendeu e concorda com as condições abaixo.

---

## 1. Empresa contratante e faturamento

- **Razão social:** [RAZAO_SOCIAL_CLIENTE]
- **CNPJ:** [CNPJ_CLIENTE]
- **Endereço:** [ENDERECO_CLIENTE], [BAIRRO_CLIENTE], [CIDADE_CLIENTE]/[UF_CLIENTE], CEP [CEP_CLIENTE]
- **Signatário do aceite:** [SIGNATARIO_ACEITE_CLIENTE], [CARGO_SIGNATARIO_ACEITE], CPF [CPF_SIGNATARIO_ACEITE], e-mail [EMAIL_SIGNATARIO_ACEITE]

1.1. A empresa acima integra o grupo econômico do Cliente e responde integralmente pela cobrança do recurso contratado neste documento. Os valores e condições dos demais contratos do Cliente não são alterados por este documento.

---

## 2. Contatos para este recurso

**Signatário do aceite eletrônico:** [SIGNATARIO_ACEITE_CLIENTE] ([CARGO_SIGNATARIO_ACEITE]) — e-mail [EMAIL_SIGNATARIO_ACEITE], telefone [TELEFONE_SIGNATARIO_ACEITE]

**Responsável operacional:** [NOME_RESPONSAVEL_OPERACIONAL] ([CARGO_RESPONSAVEL_OPERACIONAL]) — e-mail [EMAIL_RESPONSAVEL_OPERACIONAL], telefone [TELEFONE_RESPONSAVEL_OPERACIONAL]
[BLOCO_OPERACIONAIS_ADICIONAIS]
**Contato financeiro** (recebe boletos, notas fiscais e tratativas de pagamento por e-mail e WhatsApp): [NOME_CONTATO_FINANCEIRO] — e-mail [EMAIL_CONTATO_FINANCEIRO], WhatsApp [TELEFONE_CONTATO_FINANCEIRO]
[BLOCO_FINANCEIROS_ADICIONAIS]
---

## 3. Recurso contratado

| Item | Detalhamento |
|------|--------------|
| Recurso | [NOME_RECURSO] |
| Descrição | [DESCRICAO_RECURSO] |
| Valor mensal | R$ [VALOR_MENSAL] |
| Implantação | [TEXTO_IMPLANTACAO_RECURSO] |
| Data de ativação | [DATA_ATIVACAO] |
| Primeira cobrança | [TEXTO_PRIMEIRA_COBRANCA_RECURSO] |
| Dia de vencimento das mensalidades | Todo dia [DIA_VENCIMENTO_MENSAL] |
| Conta na plataforma | [TEXTO_CONTA_PLATAFORMA] |

[BLOCO_CONDICOES_ESPECIAIS]---

## 4. Vigência e cobrança

4.1. **Data de Ativação** é a data deste aceite: o recurso é liberado na plataforma dgenny no mesmo dia e a contabilização se inicia a partir dela.

4.2. [TEXTO_REGRA_PRIMEIRA_COBRANCA]

4.3. A partir do mês seguinte ao da ativação, o valor mensal é cobrado integralmente, no mesmo modelo, forma de pagamento e dia de vencimento indicados acima, junto às demais cobranças da empresa contratante.

4.4. Este recurso acompanha a vigência, a renovação e o reajuste do Termo de Adesão original. O Cliente pode encerrar apenas este recurso, a qualquer tempo, mediante aviso com 30 (trinta) dias de antecedência, sem multa própria, cessando a cobrança no ciclo seguinte ao aviso; o encerramento do Termo de Adesão original encerra também este recurso.

4.5. Aplicam-se integralmente a este recurso as demais condições do Termo de Adesão original — uso da plataforma, atraso no pagamento, responsabilidades, proteção de dados, confidencialidade, propriedade intelectual, regras gerais e foro.

---

## 5. Aceite eletrônico

Ao concluir a contratação no link recebido, o Cliente declara que leu este documento, confirmou os dados da empresa contratante, dos contatos e do recurso, e possui poderes para contratar em nome da empresa. O aceite eletrônico, registrado por link único com token individualizado, equivale à assinatura manuscrita para todos os fins legais, nos termos da Medida Provisória nº 2.200-2/2001 e da Lei nº 14.063/2020.

**Registros do aceite (para fins probatórios):**

| Dado | Valor |
|------|-------|
| Token de aceite | [TOKEN_ACEITE] |
| Data e hora do aceite (UTC-3) | [DATA_HORA_ACEITE] |
| Endereço IP do aceite | [IP_ACEITE] |
| User-Agent | [USER_AGENT_ACEITE] |
| Hash do documento | [HASH_DOCUMENTO] |

---

*Contratação registrada eletronicamente em [DATA_ACEITE] por [SIGNATARIO_ACEITE_CLIENTE] ([CARGO_SIGNATARIO_ACEITE]), em nome de [RAZAO_SOCIAL_CLIENTE], mediante token [TOKEN_ACEITE].*
`;

function buildTextoImplantacaoRecurso(data: RenderTermoAditivoData): string {
  const cent = data.valor_implantacao_centavos || 0;
  if (cent <= 0) return 'Não se aplica';
  return `R$ ${formatCentavosBR(cent)} em parcela única, cobrada junto à primeira cobrança do recurso`;
}

function buildTextoPrimeiraCobrancaRecurso(data: RenderTermoAditivoData): string {
  if (data.pro_rata_primeira_mensalidade) {
    return (
      'Proporcional (pro rata) aos dias entre a Data de Ativação e o último dia do mês da ' +
      'ativação, lançada junto à próxima fatura da empresa contratante, com descrição própria'
    );
  }
  return 'Valor mensal integral a partir do mês seguinte ao da ativação';
}

function buildTextoRegraPrimeiraCobranca(data: RenderTermoAditivoData): string {
  if (data.pro_rata_primeira_mensalidade) {
    return (
      '**Primeira cobrança (pro rata):** a primeira mensalidade deste recurso é proporcional aos ' +
      'dias entre a Data de Ativação e o último dia do mês da ativação, e é lançada junto à ' +
      'próxima fatura da empresa contratante, identificada com a descrição do recurso.'
    );
  }
  return (
    '**Primeira cobrança:** a cobrança deste recurso inicia no mês seguinte ao da ativação, ' +
    'pelo valor mensal integral; os dias do mês da ativação não são cobrados.'
  );
}

function buildTextoContaPlataforma(data: RenderTermoAditivoData): string {
  return data.conta_separada
    ? 'Conta própria, criada para a empresa contratante (acessos e dados separados da conta principal)'
    : 'Recurso adicionado à conta já existente do Cliente';
}

function buildBlocoCondicoesEspeciaisAditivo(data: RenderTermoAditivoData): string {
  const txt = (data.condicoes_especiais || '').trim();
  if (!txt) return '';
  return `**Condições especiais negociadas:** ${txt}\n\n`;
}

/** Renderiza o termo de recurso adicional. Mesma mecânica do renderTermo
 *  (hash calculado com [HASH_DOCUMENTO] literal). */
/** Renderiza preview do termo de recurso adicional no client (sem hash —
 *  backend calcula). Mesma sequência de replaces do renderTermoAditivo do
 *  cs-dgenny; [HASH_DOCUMENTO] vira placeholder, como no renderTermoPreview. */
export function renderTermoAditivoPreview(data: RenderTermoAditivoData): string {
  let md = TERMO_ADITIVO_TEMPLATE_MD;

  // Blocos derivados primeiro (ordem = espelho do backend).
  md = md.replace('[BLOCO_OPERACIONAIS_ADICIONAIS]', buildBlocoContatosAdicionais('Demais contatos operacionais', data.operacionais_adicionais, 'telefone'));
  md = md.replace('[BLOCO_FINANCEIROS_ADICIONAIS]', buildBlocoContatosAdicionais('Demais contatos financeiros', data.financeiros_adicionais, 'WhatsApp'));
  md = md.replace('[BLOCO_CONDICOES_ESPECIAIS]', buildBlocoCondicoesEspeciaisAditivo(data));
  md = md.replace('[TEXTO_IMPLANTACAO_RECURSO]', buildTextoImplantacaoRecurso(data));
  md = md.replace('[TEXTO_PRIMEIRA_COBRANCA_RECURSO]', buildTextoPrimeiraCobrancaRecurso(data));
  md = md.replace('[TEXTO_REGRA_PRIMEIRA_COBRANCA]', buildTextoRegraPrimeiraCobranca(data));
  md = md.replace('[TEXTO_CONTA_PLATAFORMA]', buildTextoContaPlataforma(data));

  md = md.replace(/\[DATA_VIGENCIA_TERMO\]/g, TERMO_ADITIVO_DATA_VIGENCIA);
  md = md.replace(/\[TOKEN_ACEITE\]/g, data.token_aceite);
  md = md.replace(/\[DATA_ACEITE\]/g, data.data_aceite_extenso);
  md = md.replace(/\[DATA_HORA_ACEITE\]/g, data.data_hora_aceite);
  md = md.replace(/\[IP_ACEITE\]/g, data.ip_aceite || '(será capturado ao aceitar)');
  md = md.replace(/\[USER_AGENT_ACEITE\]/g, data.user_agent_aceite || '(será capturado ao aceitar)');
  md = md.replace(/\[PARENT_ADHESION_ID\]/g, data.parent_adhesion_id);
  md = md.replace(/\[PARENT_DOCUMENT_HASH\]/g, data.parent_document_hash);
  md = md.replace(/\[PARENT_ACCEPTED_AT\]/g, data.parent_accepted_at);

  md = md.replace(/\[RAZAO_SOCIAL_CLIENTE\]/g, data.razao_social_cliente);
  md = md.replace(/\[CNPJ_CLIENTE\]/g, data.cnpj_cliente);
  md = md.replace(/\[ENDERECO_CLIENTE\]/g, data.endereco_cliente);
  md = md.replace(/\[BAIRRO_CLIENTE\]/g, data.bairro_cliente);
  md = md.replace(/\[CIDADE_CLIENTE\]/g, data.cidade_cliente);
  md = md.replace(/\[UF_CLIENTE\]/g, data.uf_cliente);
  md = md.replace(/\[CEP_CLIENTE\]/g, data.cep_cliente);

  md = md.replace(/\[SIGNATARIO_ACEITE_CLIENTE\]/g, data.signatario_aceite_cliente);
  md = md.replace(/\[CARGO_SIGNATARIO_ACEITE\]/g, data.cargo_signatario_aceite);
  md = md.replace(/\[CPF_SIGNATARIO_ACEITE\]/g, data.cpf_signatario_aceite);
  md = md.replace(/\[EMAIL_SIGNATARIO_ACEITE\]/g, data.email_signatario_aceite);
  md = md.replace(/\[TELEFONE_SIGNATARIO_ACEITE\]/g, data.telefone_signatario_aceite);
  md = md.replace(/\[NOME_RESPONSAVEL_OPERACIONAL\]/g, data.nome_responsavel_operacional);
  md = md.replace(/\[CARGO_RESPONSAVEL_OPERACIONAL\]/g, data.cargo_responsavel_operacional);
  md = md.replace(/\[EMAIL_RESPONSAVEL_OPERACIONAL\]/g, data.email_responsavel_operacional);
  md = md.replace(/\[TELEFONE_RESPONSAVEL_OPERACIONAL\]/g, data.telefone_responsavel_operacional);
  md = md.replace(/\[NOME_CONTATO_FINANCEIRO\]/g, data.nome_contato_financeiro);
  md = md.replace(/\[EMAIL_CONTATO_FINANCEIRO\]/g, data.email_contato_financeiro);
  md = md.replace(/\[TELEFONE_CONTATO_FINANCEIRO\]/g, data.telefone_contato_financeiro);

  md = md.replace(/\[NOME_RECURSO\]/g, data.nome_recurso);
  md = md.replace(/\[DESCRICAO_RECURSO\]/g, data.descricao_recurso || '—');
  md = md.replace(/\[VALOR_MENSAL\]/g, data.valor_mensal);
  md = md.replace(/\[DATA_ATIVACAO\]/g, data.data_ativacao);
  md = md.replace(/\[DIA_VENCIMENTO_MENSAL\]/g, String(data.dia_vencimento_mensal));

  md = md.replace(/\[HASH_DOCUMENTO\]/g, '(calculado no servidor após o aceite)');
  return md;
}
